import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import isString from 'lodash-es/isString';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { ComponentsManager, PropTypes as PropTypes$1, FieldsManager, Tracking } from '@panneau/core';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import uniqBy from 'lodash-es/uniqBy';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { getJSON } from '@folklore/fetch';
import isObject from 'lodash-es/isObject';
import { loadGoogleApi, loadGoogleMaps } from '@folklore/services';
import { useIntl } from 'react-intl';
import { getDisplayName } from '@panneau/core/utils';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useUrlGenerator } from '@folklore/routes';
export { RoutesContext, RoutesProvider, useRoutes, useUrlGenerator } from '@folklore/routes';
import { useLocation } from 'wouter';
import { TrackingContext, TrackingContainer } from '@folklore/tracking';
export { TrackingContext } from '@folklore/tracking';

var MODALS_NAMESPACE = 'modals';
var FIELDS_NAMESPACE = 'fields';
var FORMS_NAMESPACE = 'forms';
var FILTERS_NAMESPACE = 'filters';
var LISTS_NAMESPACE = 'lists';
var DISPLAYS_NAMESPACE = 'displays';
var ACTIONS_NAMESPACE = 'actions';
var BUTTONS_NAMESPACE = 'buttons';
var PAGES_NAMESPACE = 'pages';
var APP_NAMESPACE = 'app';
var ComponentsContext = /*#__PURE__*/React.createContext(null);

/**
 * Hooks
 */
var useComponentsManager = function useComponentsManager() {
  var namespace = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var manager = useContext(ComponentsContext);
  var finalManager = useMemo(function () {
    return namespace !== null ? new ComponentsManager(manager.getComponents(namespace)) : manager;
  }, [manager, namespace]);
  return finalManager;
};
var useComponents = function useComponents() {
  var namespace = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var defaultComponents = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var manager = useComponentsManager();
  return manager.getComponents(namespace) || defaultComponents;
};
var useComponent = function useComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var namespace = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var manager = useComponentsManager(namespace);
  return useMemo(function () {
    if (!isString(name)) {
      return name || defaultComponent;
    }
    return manager.getComponent(name) || defaultComponent;
  }, [manager, name, defaultComponent]);
};

/**
 * Fields hooks
 */
var useFieldsComponentsManager = function useFieldsComponentsManager() {
  return useComponentsManager(FIELDS_NAMESPACE);
};
var useFieldsComponents = function useFieldsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(FIELDS_NAMESPACE, defaultComponents);
};
var useFieldComponent = function useFieldComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, FIELDS_NAMESPACE);
};

/**
 * Forms hooks
 */
var useFormsComponentsManager = function useFormsComponentsManager() {
  return useComponentsManager(FORMS_NAMESPACE);
};
var useFormsComponents = function useFormsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(FORMS_NAMESPACE, defaultComponents);
};
var useFormComponent = function useFormComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, FORMS_NAMESPACE);
};

/**
 * Modals hooks
 */
var useModalsComponentsManager = function useModalsComponentsManager() {
  return useComponentsManager(MODALS_NAMESPACE);
};
var useModalsComponents = function useModalsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(MODALS_NAMESPACE, defaultComponents);
};
var useModalComponent = function useModalComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, MODALS_NAMESPACE);
};

/**
 * Filters hooks
 */
var useFiltersComponentsManager = function useFiltersComponentsManager() {
  return useComponentsManager(FILTERS_NAMESPACE);
};
var useFiltersComponents = function useFiltersComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(FILTERS_NAMESPACE, defaultComponents);
};
var useFilterComponent = function useFilterComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, FILTERS_NAMESPACE);
};

/**
 * Lists hooks
 */
var useListsComponentsManager = function useListsComponentsManager() {
  return useComponentsManager(LISTS_NAMESPACE);
};
var useListsComponents = function useListsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(LISTS_NAMESPACE, defaultComponents);
};
var useListComponent = function useListComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, LISTS_NAMESPACE);
};

/**
 * Displays hooks
 */
var useDisplaysComponentsManager = function useDisplaysComponentsManager() {
  return useComponentsManager(DISPLAYS_NAMESPACE);
};
var useDisplaysComponents = function useDisplaysComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(DISPLAYS_NAMESPACE, defaultComponents);
};
var useDisplayComponent = function useDisplayComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, DISPLAYS_NAMESPACE);
};

/**
 * Actions hooks
 */
var useActionsComponentsManager = function useActionsComponentsManager() {
  return useComponentsManager(ACTIONS_NAMESPACE);
};
var useActionsComponents = function useActionsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(ACTIONS_NAMESPACE, defaultComponents);
};
var useActionComponent = function useActionComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, ACTIONS_NAMESPACE);
};

/**
 * Buttons hooks
 */
var useButtonsComponentsManager = function useButtonsComponentsManager() {
  return useComponentsManager(BUTTONS_NAMESPACE);
};
var useButtonsComponents = function useButtonsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(BUTTONS_NAMESPACE, defaultComponents);
};
var useButtonComponent = function useButtonComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, BUTTONS_NAMESPACE);
};

/**
 * Pages hooks
 */
var usePagesComponentsManager = function usePagesComponentsManager() {
  return useComponentsManager(PAGES_NAMESPACE);
};
var usePagesComponents = function usePagesComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(PAGES_NAMESPACE, defaultComponents);
};
var usePageComponent = function usePageComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, PAGES_NAMESPACE);
};

/**
 * App hooks
 */
var useAppComponentsManager = function useAppComponentsManager() {
  return useComponentsManager(APP_NAMESPACE);
};
var useAppComponents = function useAppComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(APP_NAMESPACE, defaultComponents);
};
var useAppComponent = function useAppComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, APP_NAMESPACE);
};

/**
 * Provider
 */
var propTypes$d = {
  children: PropTypes.node.isRequired,
  namespace: PropTypes.string,
  manager: PropTypes.instanceOf(ComponentsManager),
  components: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.func]))
};
var defaultProps$d = {
  namespace: null,
  components: {},
  manager: null
};
var ComponentsProvider = function ComponentsProvider(_ref) {
  var components = _ref.components,
    manager = _ref.manager,
    namespace = _ref.namespace,
    children = _ref.children;
  var previousManager = useComponentsManager() || null;
  var finalManager = useMemo(function () {
    return new ComponentsManager(_objectSpread(_objectSpread(_objectSpread({}, previousManager !== null ? previousManager.getComponents() : null), manager !== null ? manager.getComponents() : null), new ComponentsManager(components).addNamespace(namespace).getComponents()));
  }, [previousManager, manager, components, namespace]);
  return /*#__PURE__*/React.createElement(ComponentsContext.Provider, {
    value: finalManager
  }, children);
};
ComponentsProvider.propTypes = propTypes$d;
ComponentsProvider.defaultProps = defaultProps$d;

var message = PropTypes.shape({
  id: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string
});
var label = PropTypes.oneOfType([message, PropTypes.node]);
PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

/**
 * Core
 */
var menuItem = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: label,
  url: PropTypes.string,
  external: PropTypes.bool,
  active: PropTypes.bool
});
PropTypes.arrayOf(menuItem);
var button = PropTypes.shape({
  id: PropTypes.string,
  label: label,
  onClick: PropTypes.func
});
PropTypes.arrayOf(button);
PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', null]);
PropTypes.oneOf(['lg', 'md', 'sm', null]);
PropTypes.oneOf(['button', 'submit']);
PropTypes.oneOf(['start', 'end']);
PropTypes.oneOf(['lg', 'sm', null]);
PropTypes.oneOf(['loading', 'success', 'error', null]);
var fieldOption = PropTypes.shape({
  value: PropTypes.string,
  label: PropTypes.string
});
PropTypes.arrayOf(fieldOption);
var toggle = PropTypes.shape({
  key: PropTypes.string,
  label: PropTypes.string
});
PropTypes.arrayOf(toggle);
var tableColumn = PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
  id: PropTypes.string,
  component: PropTypes.string,
  path: PropTypes.string,
  field: PropTypes.string,
  columnClassName: PropTypes.string
})]);
PropTypes.arrayOf(tableColumn);

/**
 * Panneau Definitions
 */
var routes = PropTypes.shape({
  'resources.index': PropTypes.string.isRequired,
  'resources.create': PropTypes.string.isRequired,
  'resources.store': PropTypes.string.isRequired,
  'resources.show': PropTypes.string.isRequired,
  'resources.edit': PropTypes.string.isRequired,
  'resources.update': PropTypes.string.isRequired,
  'resources.delete': PropTypes.string.isRequired,
  'resources.destroy': PropTypes.string.isRequired,
  'resources.duplicate': PropTypes.string.isRequired,
  'resources.clone': PropTypes.string.isRequired,
  'resources.restore': PropTypes.string.isRequired
});
var intl = PropTypes.shape({
  locale: PropTypes.string,
  messages: PropTypes.objectOf(PropTypes.string),
  values: PropTypes.objectOf(PropTypes.string)
});
var page = PropTypes.shape({
  component: PropTypes.string.isRequired
});
var pages = PropTypes.objectOf(page);
var field = PropTypes.shape({
  name: PropTypes.string,
  // Not required on localized fields
  type: PropTypes.string,
  component: PropTypes.string,
  label: PropTypes.node
});
var fields = PropTypes.arrayOf(field);
var form = PropTypes.shape({
  // type: PropTypes.string.isRequired,
  title: PropTypes.isRequired,
  fields: fields
});
PropTypes.arrayOf(form);
var resource = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  intl: intl,
  fields: fields,
  forms: PropTypes.shape({}),
  shows_in_navbar: PropTypes.bool
});
var resources = PropTypes.arrayOf(resource);
var panneauDefinition = PropTypes.shape({
  name: PropTypes.string,
  resources: resources,
  routes: routes,
  pages: pages,
  intl: intl
  // intl,
});
var definition = PropTypes.shape({
  name: PropTypes.string
  // resources,
  // routes,
  // intl,
});
var item = PropTypes.shape({
  id: PropTypes.string.isRequired
});
PropTypes.arrayOf(item);
var user = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
});
PropTypes.arrayOf(user);
var selectOption = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({
  value: PropTypes.any,
  // eslint-disable-line
  label: label
}), PropTypes.object]);
PropTypes.arrayOf(selectOption);
PropTypes.oneOf(['valid', 'invalid', 'loading', null]);
var formError = PropTypes.shape({
  message: PropTypes.string
});
PropTypes.arrayOf(formError);
PropTypes.shape({
  tus: PropTypes.object
}); // eslint-disable-line

PropTypes.string;
var breadcrumb = PropTypes.shape({
  url: PropTypes.string
});
PropTypes.arrayOf(breadcrumb);
var font = PropTypes.shape({
  // url: PropTypes.string,
});
PropTypes.arrayOf(font);
PropTypes.shape({});
var modal = PropTypes.shape({
  name: PropTypes.string
});
PropTypes.arrayOf(modal);
var filter = PropTypes.shape({
  id: PropTypes.string,
  component: PropTypes.string
});
PropTypes.arrayOf(filter);
var media = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  thumbnail_url: PropTypes.string
});
PropTypes.arrayOf(media);

/* eslint-disable react/jsx-props-no-spreading */
var DefinitionContext = /*#__PURE__*/React.createContext(null);
var useDefinitionContext = function useDefinitionContext() {
  return useContext(DefinitionContext);
};
var useDefinition = function useDefinition() {
  var _useDefinitionContext = useDefinitionContext(),
    _useDefinitionContext2 = _useDefinitionContext.definition,
    definition = _useDefinitionContext2 === void 0 ? null : _useDefinitionContext2;
  return definition;
};
var propTypes$c = {
  definition: definition.isRequired,
  children: PropTypes.node.isRequired
};
var defaultProps$c = {};
var DefinitionProvider = function DefinitionProvider(_ref) {
  var definition = _ref.definition,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(DefinitionContext.Provider, {
    value: definition
  }, children);
};
DefinitionProvider.propTypes = propTypes$c;
DefinitionProvider.defaultProps = defaultProps$c;

var FieldsContext = /*#__PURE__*/React.createContext(null);
var useFieldsManager = function useFieldsManager() {
  return useContext(FieldsContext);
};
var useField = function useField(id) {
  var manager = useFieldsManager();
  return manager.getDefinition(id);
};
var useFields = function useFields() {
  var manager = useFieldsManager();
  return manager.getDefinitions();
};
var propTypes$b = {
  fields: PropTypes$1.fields,
  manager: PropTypes.instanceOf(FieldsManager),
  children: PropTypes.node.isRequired
};
var defaultProps$b = {
  fields: null,
  manager: null
};
var FieldsProvider = function FieldsProvider(_ref) {
  var fields = _ref.fields,
    manager = _ref.manager,
    children = _ref.children;
  var previousManager = useFieldsManager() || null;
  var finalManager = useMemo(function () {
    var newFields = uniqBy([].concat(_toConsumableArray(fields || []), _toConsumableArray(manager !== null ? manager.getDefinitions() : []), _toConsumableArray(previousManager !== null ? previousManager.getDefinitions() : [])), function (_ref2) {
      var id = _ref2.id;
      return id;
    }).reverse();
    return new FieldsManager(newFields);
  }, [previousManager, manager, fields]);
  var components = useMemo(function () {
    var newComponents = finalManager.getComponents();
    return Object.keys(newComponents).reduce(function (map, id) {
      var component = newComponents[id];
      return isString(component) ? map : _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, id, component));
    }, {});
  }, [finalManager]);
  return /*#__PURE__*/React.createElement(FieldsContext.Provider, {
    value: finalManager
  }, /*#__PURE__*/React.createElement(ComponentsProvider, {
    namespace: FIELDS_NAMESPACE,
    components: components
  }, children));
};
FieldsProvider.propTypes = propTypes$b;
FieldsProvider.defaultProps = defaultProps$b;

/* eslint-disable react/jsx-props-no-spreading */
var GoogleKeysContext = /*#__PURE__*/React.createContext({
  apiKey: null
});
var useGoogleKeys = function useGoogleKeys() {
  return useContext(GoogleKeysContext);
};
var propTypes$a = {
  children: PropTypes.node.isRequired,
  apiKey: PropTypes.string // .isRequired,
};
var defaultProps$a = {
  apiKey: null
};
var GoogleKeysProvider = function GoogleKeysProvider(_ref) {
  var children = _ref.children,
    apiKey = _ref.apiKey;
  var _useGoogleKeys = useGoogleKeys(),
    previousApiKey = _useGoogleKeys.apiKey;
  var value = useMemo(function () {
    return {
      apiKey: apiKey || previousApiKey
    };
  }, [previousApiKey, apiKey]);
  return /*#__PURE__*/React.createElement(GoogleKeysContext.Provider, {
    value: value
  }, children);
};
GoogleKeysProvider.propTypes = propTypes$a;
GoogleKeysProvider.defaultProps = defaultProps$a;

var FontsContext = /*#__PURE__*/React.createContext({
  systemFonts: null,
  googleFonts: null,
  customFonts: null
});
var useGoogleFonts = function useGoogleFonts() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$setFonts = _ref.setFonts,
    setFonts = _ref$setFonts === void 0 ? null : _ref$setFonts;
  var _useGoogleKeys = useGoogleKeys(),
    apiKey = _useGoogleKeys.apiKey;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    googleFonts = _useState2[0],
    setGoogleFonts = _useState2[1];
  useEffect(function () {
    var canceled = false;
    if (apiKey !== null && !disabled) {
      getJSON("https://www.googleapis.com/webfonts/v1/webfonts?key=".concat(apiKey, "&sort=popularity")).then(function (_ref2) {
        var _ref2$items = _ref2.items,
          items = _ref2$items === void 0 ? [] : _ref2$items;
        if (!canceled) {
          var newFonts = items.map(function (it) {
            return {
              type: 'google',
              name: it.family,
              variants: it.variants
            };
          });
          if (setFonts !== null) {
            setFonts(newFonts);
          } else {
            setGoogleFonts(newFonts);
          }
        }
      });
    }
    return function () {
      canceled = true;
    };
  }, [apiKey, disabled, setFonts, setGoogleFonts]);
  return googleFonts;
};
var useFonts = function useFonts() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref3$withoutGoogleFo = _ref3.withoutGoogleFonts,
    withoutGoogleFonts = _ref3$withoutGoogleFo === void 0 ? false : _ref3$withoutGoogleFo;
  var _useContext = useContext(FontsContext),
    _useContext$setGoogle = _useContext.setGoogleFonts,
    setGoogleFonts = _useContext$setGoogle === void 0 ? null : _useContext$setGoogle,
    _useContext$systemFon = _useContext.systemFonts,
    systemFonts = _useContext$systemFon === void 0 ? null : _useContext$systemFon,
    _useContext$googleFon = _useContext.googleFonts,
    googleFonts = _useContext$googleFon === void 0 ? null : _useContext$googleFon,
    _useContext$customFon = _useContext.customFonts,
    customFonts = _useContext$customFon === void 0 ? null : _useContext$customFon;
  useGoogleFonts({
    disabled: withoutGoogleFonts || googleFonts !== null && googleFonts.length > 0,
    setFonts: setGoogleFonts
  });
  var fonts = useMemo(function () {
    return {
      systemFonts: systemFonts,
      googleFonts: googleFonts,
      customFonts: customFonts
    };
  }, [systemFonts, googleFonts, customFonts]);
  return fonts;
};
var propTypes$9 = {
  children: PropTypes.node.isRequired,
  systemFonts: PropTypes$1.fonts,
  customFonts: PropTypes$1.fonts
};
var defaultProps$9 = {
  systemFonts: ['Arial', 'Courier', 'Georgia', 'Times New Roman', 'Verdana'],
  customFonts: null
};
var FontsProvider = function FontsProvider(_ref4) {
  var systemFonts = _ref4.systemFonts,
    customFonts = _ref4.customFonts,
    children = _ref4.children;
  var _useFonts = useFonts(),
    _useFonts$systemFonts = _useFonts.systemFonts,
    previousSystemFonts = _useFonts$systemFonts === void 0 ? null : _useFonts$systemFonts,
    _useFonts$googleFonts = _useFonts.googleFonts,
    previousGoogleFonts = _useFonts$googleFonts === void 0 ? null : _useFonts$googleFonts,
    previousCustomFonts = _useFonts.customFonts;
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    googleFonts = _useState4[0],
    setGoogleFonts = _useState4[1];
  var fonts = useMemo(function () {
    return {
      systemFonts: uniqBy([].concat(_toConsumableArray(previousSystemFonts || []), _toConsumableArray(systemFonts || [])), function (font) {
        return isObject(font) ? font.name : font;
      }),
      googleFonts: uniqBy([].concat(_toConsumableArray(previousGoogleFonts || []), _toConsumableArray(googleFonts || [])), function (font) {
        return isObject(font) ? font.name : font;
      }),
      customFonts: uniqBy([].concat(_toConsumableArray(previousCustomFonts || []), _toConsumableArray(customFonts || [])), function (font) {
        return isObject(font) ? font.name : font;
      }),
      setGoogleFonts: setGoogleFonts
    };
  }, [previousSystemFonts, previousGoogleFonts, previousCustomFonts, customFonts, systemFonts, googleFonts, setGoogleFonts]);
  return /*#__PURE__*/React.createElement(FontsContext.Provider, {
    value: fonts
  }, children);
};
FontsProvider.propTypes = propTypes$9;
FontsProvider.defaultProps = defaultProps$9;

/* eslint-disable react/jsx-props-no-spreading */
var FormContext = /*#__PURE__*/React.createContext(null);
var useForm = function useForm() {
  return useContext(FormContext);
};
var useFormValue = function useFormValue() {
  var _useForm = useForm(),
    value = _useForm.value;
  return value;
};
var useFormSetValue = function useFormSetValue() {
  var _useForm2 = useForm(),
    setValue = _useForm2.setValue;
  return setValue;
};
var propTypes$8 = {
  value: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  setValue: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};
var defaultProps$8 = {};
var FormProvider = function FormProvider(_ref) {
  var value = _ref.value,
    setValue = _ref.setValue,
    children = _ref.children;
  var values = useMemo(function () {
    return {
      value: value,
      setValue: setValue
    };
  }, [value, setValue]);
  return /*#__PURE__*/React.createElement(FormContext.Provider, {
    value: values
  }, children);
};
FormProvider.propTypes = propTypes$8;
FormProvider.defaultProps = defaultProps$8;

var GoogleApiClientContext = /*#__PURE__*/React.createContext(null);
var useGoogleApiClient = function useGoogleApiClient() {
  return useContext(GoogleApiClientContext);
};
var withGoogleApiClient = function withGoogleApiClient(WrappedComponent) {
  var getDisplayName = function getDisplayName(_ref) {
    var _ref$displayName = _ref.displayName,
      displayName = _ref$displayName === void 0 ? null : _ref$displayName,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? null : _ref$name;
    return displayName || name || 'Component';
  };
  var WithGoogleApiClientComponent = function WithGoogleApiClientComponent(props) {
    return /*#__PURE__*/React.createElement(GoogleApiClientContext.Consumer, null, function (client) {
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        googleApiClient: client
      }, props));
    });
  };
  WithGoogleApiClientComponent.displayName = "WithGoogleApiClient(".concat(getDisplayName(WrappedComponent), ")");
  return WithGoogleApiClientComponent;
};
var propTypes$7 = {
  children: PropTypes.node.isRequired
};
var defaultProps$7 = {};
var GoogleApiClientProvider = function GoogleApiClientProvider(_ref2) {
  var children = _ref2.children;
  var _useGoogleKeys = useGoogleKeys(),
    apiKey = _useGoogleKeys.apiKey;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    client = _useState2[0],
    setClient = _useState2[1];
  useEffect(function () {
    loadGoogleApi().then(function (gapi) {
      return gapi.client.init({
        apiKey: apiKey
      }).then(function () {
        return gapi;
      });
    }).then(function (gapi) {
      return setClient(gapi);
    });
  }, [apiKey, setClient]);
  return /*#__PURE__*/React.createElement(GoogleApiClientContext.Provider, {
    value: client
  }, children);
};
GoogleApiClientProvider.propTypes = propTypes$7;
GoogleApiClientProvider.defaultProps = defaultProps$7;

var GoogleMapsClientContext = /*#__PURE__*/React.createContext(null);
var useGoogleMapsClient = function useGoogleMapsClient() {
  return useContext(GoogleMapsClientContext);
};
var withGoogleMapsClient = function withGoogleMapsClient(WrappedComponent) {
  var getDisplayName = function getDisplayName(_ref) {
    var _ref$displayName = _ref.displayName,
      displayName = _ref$displayName === void 0 ? null : _ref$displayName,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? null : _ref$name;
    return displayName || name || 'Component';
  };
  var WithGoogleMapsClientComponent = function WithGoogleMapsClientComponent(props) {
    return /*#__PURE__*/React.createElement(GoogleMapsClientContext.Consumer, null, function (client) {
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        googleApiClient: client
      }, props));
    });
  };
  WithGoogleMapsClientComponent.displayName = "WithGoogleMapsClient(".concat(getDisplayName(WrappedComponent), ")");
  return WithGoogleMapsClientComponent;
};
var propTypes$6 = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.string,
  libraries: PropTypes.arrayOf(PropTypes.string)
};
var defaultProps$6 = {
  locale: 'fr',
  libraries: null
};
var GoogleMapsClientProvider = function GoogleMapsClientProvider(_ref2) {
  var children = _ref2.children,
    locale = _ref2.locale,
    libraries = _ref2.libraries;
  var _useGoogleKeys = useGoogleKeys(),
    apiKey = _useGoogleKeys.apiKey;
  var exisitingClient = useGoogleMapsClient();
  var _useState = useState(exisitingClient),
    _useState2 = _slicedToArray(_useState, 2),
    client = _useState2[0],
    setClient = _useState2[1];
  useEffect(function () {
    if (exisitingClient === null) {
      loadGoogleMaps({
        apiKey: apiKey,
        locale: locale,
        libraries: libraries
      }).then(function (newClient) {
        setClient(newClient);
      });
    }
  }, [apiKey, locale, libraries, setClient, exisitingClient]);
  return /*#__PURE__*/React.createElement(GoogleMapsClientContext.Provider, {
    value: client
  }, children);
};
GoogleMapsClientProvider.propTypes = propTypes$6;
GoogleMapsClientProvider.defaultProps = defaultProps$6;

/* eslint-disable react/jsx-props-no-spreading */
var defaultLocales = ['en', 'fr'];
var LocalesContext = /*#__PURE__*/React.createContext({
  locales: defaultLocales
});
var useLocales = function useLocales() {
  var _useContext = useContext(LocalesContext),
    locales = _useContext.locales;
  return locales;
};
var useOtherLocales = function useOtherLocales() {
  var _useLocales = useLocales(),
    locales = _useLocales.locales;
  var _useIntl = useIntl(),
    locale = _useIntl.locale;
  var otherLocales = useMemo(function () {
    return locales.filter(function (it) {
      return it !== locale;
    });
  }, [locales, locale]);
  return otherLocales;
};
var propTypes$5 = {
  locales: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired
};
var defaultProps$5 = {
  locales: []
};
var LocalesProvider = function LocalesProvider(_ref) {
  var locales = _ref.locales,
    children = _ref.children;
  var value = useMemo(function () {
    return {
      locales: locales
    };
  }, [locales]);
  return /*#__PURE__*/React.createElement(LocalesContext.Provider, {
    value: value
  }, children);
};
LocalesProvider.propTypes = propTypes$5;
LocalesProvider.defaultProps = defaultProps$5;

// import { getDisplayName } from '../utils';

var ModalContext = /*#__PURE__*/React.createContext(null);
var useModal = function useModal() {
  return useContext(ModalContext) || {};
};
var propTypes$4 = {
  children: PropTypes.node.isRequired,
  container: PropTypes.object // eslint-disable-line
};
var defaultProps$4 = {
  container: null
};
var ModalProvider = function ModalProvider(_ref) {
  var children = _ref.children,
    initialContainer = _ref.container;
  var _useState = useState(initialContainer),
    _useState2 = _slicedToArray(_useState, 2),
    container = _useState2[0],
    setContainer = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    modals = _useState4[0],
    setModals = _useState4[1];
  var modalsRef = useRef(modals);
  var register = useCallback(function (id) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var _modalsRef$current = modalsRef.current,
      currentModals = _modalsRef$current === void 0 ? [] : _modalsRef$current;
    var newModals = [].concat(_toConsumableArray(currentModals), [_objectSpread({
      id: id
    }, data)]);
    setModals(newModals);
    modalsRef.current = newModals;
  }, [setModals]);
  var unregister = useCallback(function () {
    var id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var _modalsRef$current2 = modalsRef.current,
      currentModals = _modalsRef$current2 === void 0 ? [] : _modalsRef$current2;
    var foundIndex = currentModals.findIndex(function (_ref2) {
      var modalId = _ref2.id;
      return modalId === id;
    });
    if (foundIndex !== -1) {
      var _ref3 = data || {},
        _ref3$onClose = _ref3.onClose,
        onClose = _ref3$onClose === void 0 ? null : _ref3$onClose;
      if (onClose !== null) {
        onClose();
      }
      var newModals = currentModals.filter(function (_ref4) {
        var modalId = _ref4.id;
        return modalId !== id;
      });
      setModals(newModals);
      modalsRef.current = newModals;
    }
  }, [setModals]);
  var closeLastModal = useCallback(function () {
    var lastModal = modals.pop() || null;
    if (lastModal !== null) {
      var _ref5 = lastModal || {},
        _ref5$id = _ref5.id,
        lastModalId = _ref5$id === void 0 ? null : _ref5$id,
        _ref5$onClose = _ref5.onClose,
        onClose = _ref5$onClose === void 0 ? null : _ref5$onClose;
      if (onClose !== null) {
        onClose();
      }
      var newModals = modals.filter(function (_ref6) {
        var modalId = _ref6.id;
        return modalId !== lastModalId;
      });
      setModals(newModals);
      modalsRef.current = newModals;
    }
  }, [modals, setModals]);
  var getModalById = useCallback(function (modalId) {
    return (modals || []).find(function (modal) {
      return (modal === null || modal === void 0 ? void 0 : modal.id) === modalId;
    }) || null;
  }, [modals]);
  var value = useMemo(function () {
    return {
      modals: modals,
      container: container,
      setContainer: setContainer,
      register: register,
      unregister: unregister,
      closeLastModal: closeLastModal,
      getModalById: getModalById
    };
  }, [modals, container, setContainer, register, unregister, closeLastModal, getModalById]);
  return /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: value
  }, children);
};
ModalProvider.propTypes = propTypes$4;
ModalProvider.defaultProps = defaultProps$4;

var PanelsContext = /*#__PURE__*/React.createContext({
  container: null
});
var usePanels = function usePanels() {
  return useContext(PanelsContext) || {};
};
var withPanels = function withPanels(WrappedComponent) {
  var WithPanelsComponent = function WithPanelsComponent(props) {
    return /*#__PURE__*/React.createElement(PanelsContext.Consumer, null, function (_ref) {
      var panels = _ref.panels,
        setContainer = _ref.setContainer,
        container = _ref.container,
        register = _ref.register,
        unregister = _ref.unregister;
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        panelsContainer: container,
        setPanelsContainer: setContainer,
        panels: panels,
        registerPanel: register,
        unregisterPanel: unregister
      }, props));
    });
  };
  WithPanelsComponent.displayName = "WithPanels(".concat(getDisplayName(WrappedComponent), ")");
  return WithPanelsComponent;
};
var propTypes$3 = {
  children: PropTypes.node.isRequired,
  container: PropTypes.object // eslint-disable-line
};
var defaultProps$3 = {
  container: null
};
var PanelsProvider = function PanelsProvider(_ref2) {
  var children = _ref2.children,
    initialContainer = _ref2.container;
  var _useState = useState(initialContainer),
    _useState2 = _slicedToArray(_useState, 2),
    container = _useState2[0],
    setContainer = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    panels = _useState4[0],
    setPanels = _useState4[1];
  var panelsRef = useRef(panels);
  var register = useCallback(function (id) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var currentPanels = panelsRef.current;
    var newPanels = [].concat(_toConsumableArray(currentPanels), [_objectSpread({
      id: id
    }, data)]);
    setPanels(newPanels);
    panelsRef.current = newPanels;
  }, [panels, setPanels]);
  var unregister = useCallback(function (id) {
    var currentPanels = panelsRef.current;
    var foundIndex = currentPanels.findIndex(function (_ref3) {
      var modalId = _ref3.id;
      return modalId === id;
    });
    if (foundIndex !== -1) {
      var newPanels = currentPanels.filter(function (_ref4) {
        var modalId = _ref4.id;
        return modalId !== id;
      });
      setPanels(newPanels);
      panelsRef.current = newPanels;
    }
  }, [panels, setPanels]);
  var value = useMemo(function () {
    return {
      panels: panels,
      setContainer: setContainer,
      container: container,
      register: register,
      unregister: unregister
    };
  }, [panels, setContainer, container, register, unregister]);
  return /*#__PURE__*/React.createElement(PanelsContext.Provider, {
    value: value
  }, children);
};
PanelsProvider.propTypes = propTypes$3;
PanelsProvider.defaultProps = defaultProps$3;

var _excluded = ["component"];
var PanneauContext = /*#__PURE__*/React.createContext(null);
var usePanneau = function usePanneau() {
  return useContext(PanneauContext);
};
var usePanneauResources = function usePanneauResources() {
  var _ref = usePanneau() || {},
    _ref$resources = _ref.resources,
    resources = _ref$resources === void 0 ? [] : _ref$resources;
  return resources;
};
var usePanneauResource = function usePanneauResource(id) {
  var resources = usePanneauResources();
  return resources.find(function (it) {
    return it.id === id;
  }) || null;
};
var usePanneauColorScheme = function usePanneauColorScheme() {
  var _ref2 = usePanneau() || {},
    _ref2$theme = _ref2.theme,
    theme = _ref2$theme === void 0 ? {} : _ref2$theme;
  var _ref3 = theme || {},
    _ref3$colorScheme = _ref3.colorScheme,
    colorScheme = _ref3$colorScheme === void 0 ? 'light' : _ref3$colorScheme;
  if (colorScheme === null || colorScheme === 'light' || colorScheme === 'dark') {
    return colorScheme === 'dark' ? {
      theme: 'dark',
      background: 'dark',
      text: 'light'
    } : {
      theme: 'light',
      background: 'light',
      text: 'dark'
    };
  }
  return {
    theme: colorScheme,
    background: null,
    text: null
  };
};
var usePanneauComponents = function usePanneauComponents() {
  var _ref4 = usePanneau() || {},
    _ref4$components = _ref4.components,
    components = _ref4$components === void 0 ? {} : _ref4$components;
  return components;
};
var usePanneauComponent = function usePanneauComponent(namespace, name) {
  var _ref5 = usePanneau() || {},
    _ref5$components = _ref5.components,
    components = _ref5$components === void 0 ? {} : _ref5$components;
  var path = namespace !== null ? "".concat(namespace, ".").concat(name) : name || null;
  var component = components[path] || null;
  if (isString(component)) {
    return component;
  }
  if (isObject(component) && isString(component === null || component === void 0 ? void 0 : component.componnent)) {
    // eslint-disable-next-line no-unused-vars
    component.component;
      _objectWithoutProperties(component, _excluded);
    return component.component;
  }
  return null;
};
var usePanneauAuth = function usePanneauAuth() {
  var _ref6 = usePanneau() || {},
    _ref6$auth = _ref6.auth,
    auth = _ref6$auth === void 0 ? {} : _ref6$auth;
  return auth;
};
var usePanneauSettings = function usePanneauSettings() {
  var _ref7 = usePanneau() || {},
    _ref7$settings = _ref7.settings,
    settings = _ref7$settings === void 0 ? {} : _ref7$settings;
  return settings;
};
var propTypes$2 = {
  definition: panneauDefinition.isRequired,
  children: PropTypes.node.isRequired
};
var defaultProps$2 = {};
var PanneauProvider = function PanneauProvider(_ref8) {
  var definition = _ref8.definition,
    children = _ref8.children;
  return /*#__PURE__*/React.createElement(PanneauContext.Provider, {
    value: definition
  }, children);
};
PanneauProvider.propTypes = propTypes$2;
PanneauProvider.defaultProps = defaultProps$2;

/* eslint-disable react/jsx-props-no-spreading */
var ResourceContext = /*#__PURE__*/React.createContext(null);
var useResource = function useResource() {
  return useContext(ResourceContext);
};
var propTypes$1 = {
  resource: resource.isRequired,
  children: PropTypes.node.isRequired
};
var defaultProps$1 = {};
var ResourceProvider = function ResourceProvider(_ref) {
  var resource = _ref.resource,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(ResourceContext.Provider, {
    value: resource
  }, children);
};
ResourceProvider.propTypes = propTypes$1;
ResourceProvider.defaultProps = defaultProps$1;

var useRoutePush = function useRoutePush() {
  var url = useUrlGenerator();
  var _useLocation = useLocation(),
    _useLocation2 = _slicedToArray(_useLocation, 2),
    navigate = _useLocation2[1];
  var push = useCallback(function (route, data) {
    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }
    if (isString(route)) {
      navigate.apply(void 0, [url(route, data)].concat(args));
    } else {
      var _ref = route || {},
        _ref$pathname = _ref.pathname,
        pathname = _ref$pathname === void 0 ? null : _ref$pathname,
        _ref$search = _ref.search,
        search = _ref$search === void 0 ? null : _ref$search;
      navigate.apply(void 0, ["".concat(url(pathname, data)).concat(search !== null ? "?".concat(search) : '')].concat(args));
    }
  }, [navigate, url]);
  return push;
};
var useRouteBack = function useRouteBack() {
  var url = useUrlGenerator();
  var _useLocation3 = useLocation(),
    _useLocation4 = _slicedToArray(_useLocation3, 2),
    navigate = _useLocation4[1];
  var back = useCallback(function () {
    return navigate(-1);
  }, [navigate, url]);
  return back;
};

/* eslint-disable react/jsx-props-no-spreading */
var useTracking = function useTracking() {
  return useContext(TrackingContext);
};
var propTypes = {
  children: PropTypes.node.isRequired,
  variables: PropTypes$1.trackingVariables
};
var defaultProps = {
  variables: null
};
var TrackingProvider = function TrackingProvider(_ref) {
  var variables = _ref.variables,
    children = _ref.children;
  var contextTracking = useTracking() || null;
  var tracking = useMemo(function () {
    if (contextTracking !== null) {
      contextTracking.setVariables(variables);
      return contextTracking;
    }
    return new Tracking({
      variables: variables
    });
  }, [contextTracking, variables]);
  return /*#__PURE__*/React.createElement(TrackingContainer, {
    tracking: tracking
  }, children);
};
TrackingProvider.propTypes = propTypes;
TrackingProvider.defaultProps = defaultProps;

export { ACTIONS_NAMESPACE, APP_NAMESPACE, BUTTONS_NAMESPACE, ComponentsContext, ComponentsProvider, DISPLAYS_NAMESPACE, DefinitionProvider, FIELDS_NAMESPACE, FILTERS_NAMESPACE, FORMS_NAMESPACE, FieldsContext, FieldsProvider, FontsContext, FontsProvider, FormProvider, GoogleApiClientContext, GoogleApiClientProvider, GoogleKeysContext, GoogleKeysProvider, GoogleMapsClientContext, GoogleMapsClientProvider, LISTS_NAMESPACE, LocalesContext, LocalesProvider, MODALS_NAMESPACE, ModalContext, ModalProvider, PAGES_NAMESPACE, PanelsContext, PanelsProvider, PanneauProvider, ResourceProvider, TrackingProvider, useActionComponent, useActionsComponents, useActionsComponentsManager, useAppComponent, useAppComponents, useAppComponentsManager, useButtonComponent, useButtonsComponents, useButtonsComponentsManager, useComponent, useComponents, useComponentsManager, useDefinition, useDefinitionContext, useDisplayComponent, useDisplaysComponents, useDisplaysComponentsManager, useField, useFieldComponent, useFields, useFieldsComponents, useFieldsComponentsManager, useFieldsManager, useFilterComponent, useFiltersComponents, useFiltersComponentsManager, useFonts, useForm, useFormComponent, useFormSetValue, useFormValue, useFormsComponents, useFormsComponentsManager, useGoogleApiClient, useGoogleFonts, useGoogleKeys, useGoogleMapsClient, useListComponent, useListsComponents, useListsComponentsManager, useLocales, useModal, useModalComponent, useModalsComponents, useModalsComponentsManager, useOtherLocales, usePageComponent, usePagesComponents, usePagesComponentsManager, usePanels, usePanneau, usePanneauAuth, usePanneauColorScheme, usePanneauComponent, usePanneauComponents, usePanneauResource, usePanneauResources, usePanneauSettings, useResource, useRouteBack, useRoutePush, useTracking, withGoogleApiClient, withGoogleMapsClient, withPanels };
